import React, { memo, useLayoutEffect, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import style from '../../../../../Block/Styles/index.module.scss'
import { IBlockProps } from '../../../../../Block/types'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { useAppSelector } from '../../../../../../../hooks/useAppSelector'
import { selectRuleConfigBit } from '../../../../../../../store/selectors/ruleCardSelectors'
import { generateAriaId } from '../../../../../../ActionsBar/utils/generateAriaId'
import { useExpand } from '../../../../../../../hooks/useExpand'
import { getLabel } from '../../../../../../../utils/getLabel'
import { ViewOnlyWrapper } from '../../../../../../ViewOnly/ViewOnlyWrapper'
import { DescriptionBtnWithPopup } from '../../../../../../buttons/PromptBtn/DescriptionBtnWithPopup'
import { BlockDataBtn } from '../../../../../../buttons/BlockDataBnt'
import { blockConstructor } from '../../../../../Block/control'
import {
    disableRuleBlock,
    enableRuleBlock,
    initRuleEmptyData,
} from '../../../../../../../store/actions/ruleCardActions'

export const RuleBlock = memo(function RuleBlock({
    dataPath,
    pathWithElementID,
    elements,
    title,
    className,
    dataSchema = {},
    name,
    classNameBody,
    wrapper,
    isParentDisabled,
    isViewOnly,
    element_id,
    inCard,
    isRuleActive,
}: IBlockProps) {
    const { is_required: isRequired, is_auxiliary: isCollapsed } = dataSchema
    const dispatch = useAppDispatch()

    const path = [...dataPath.split(PATH_JOINER), name]
    const configBit = useAppSelector(selectRuleConfigBit(path))
    const [blockState, setBlockState] = useState<boolean>(
        !!isRequired || !!configBit
    )

    const childrenPath = blockState && !isParentDisabled ? path : []
    const dataProp = configBit ?? {}
    const ariaID = generateAriaId()
    const localId = element_id ? `${element_id}-${name}` : `${path}-${name}`

    const { expandBtn, expanded, toggle } = useExpand({
        isExpanded: !isCollapsed,
        uniqueID: localId,
        ariaID,
        className: `${style.button} alwaysActive`,
    })

    const label = getLabel(name, title)
    const styleHeader: string[] = [
        style.header,
        inCard && !isRuleActive ? 'rule-inactive' : 'app-bg-navi-blue',
        'rounded-top',
    ]
    const modifiedPathWithElementID = [
        ...(pathWithElementID?.split(PATH_JOINER) ?? []),
        name,
    ]
    const errorKey = modifiedPathWithElementID.join(PATH_JOINER)

    const onBlockStateToggle = () => {
        setBlockState((isEnabled) => {
            if (isEnabled) {
                dispatch(disableRuleBlock({ path, errorKeyName: errorKey }))
            }
            if (!isEnabled && !isParentDisabled && !configBit) {
                dispatch(enableRuleBlock({ path, data: dataProp }))
            }
            return !isEnabled
        })
    }

    useLayoutEffect(() => {
        if (isRequired && !configBit) {
            dispatch(initRuleEmptyData({ schema: dataSchema, name, path }))
        }
        if (!isRequired && !configBit) {
            dispatch(disableRuleBlock({ path, errorKeyName: errorKey }))
        }
    }, []) // eslint-disable-line

    return (
        <ViewOnlyWrapper
            disableEvents={!!isViewOnly}
            style={{ maxWidth: '600px' }}
            className={`${className} ${
                !wrapper
                    ? 'w-50 flex-item'
                    : 'w-100 border-left border-right border-bottom'
            } mw-350 d-flex flex-column rounded border mt-2 mb-2 position-relative me-4 align-self-start`}
        >
            <div className={styleHeader.join(' ')}>
                <div
                    className="w-100"
                    onClick={toggle}
                >
                    <h4
                        className={`${style.title} ${!title && style.capitalized}`}
                    >
                        {wrapper === 'block_dictionary_card' ? (
                            <span style={{ opacity: 0 }}>{name}</span>
                        ) : (
                            label
                        )}
                    </h4>
                </div>
                {dataSchema.description && (
                    <DescriptionBtnWithPopup
                        type="small"
                        header={label}
                        description={dataSchema.description}
                        className="ms-auto me-2 text-white"
                    />
                )}
                {!isRequired && (
                    <BlockDataBtn
                        state={blockState}
                        onClick={onBlockStateToggle}
                    />
                )}
                {expandBtn}
            </div>
            <Collapse in={expanded}>
                <div className="p-relative">
                    {!blockState && !isParentDisabled && !isRequired && (
                        <div className={style.blockOverlay} />
                    )}
                    <div
                        className={`${!classNameBody ? 'p-3' : classNameBody} ${
                            inCard && !isRuleActive
                                ? 'rule-inactive-block'
                                : 'app-bg-primary'
                        } rounded-bottom`}
                        id={ariaID}
                    >
                        {elements
                            ? elements?.map(
                                  (element: JSX.Element, i: number) => (
                                      <div key={i}>{element}</div>
                                  )
                              )
                            : blockConstructor({
                                  dataSchema,
                                  data: configBit,
                                  dataProp,
                                  name,
                                  builderType: 'ruleUiBuilder',
                                  childrenPath: childrenPath.join(PATH_JOINER),
                                  dataPath,
                                  blockState,
                                  levelDeep: 2,
                                  element_id,
                                  inCard,
                                  isRuleActive,
                                  pathWithElementID:
                                      modifiedPathWithElementID.join(
                                          PATH_JOINER
                                      ),
                              })}
                    </div>
                </div>
            </Collapse>
        </ViewOnlyWrapper>
    )
})
